import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, filter, distinctUntilChanged } from 'rxjs/operators';
import { GameDayModel } from '../../models/gameday-model';
import { toMillion } from '../../gameday/components/excel-export/excel.formatter';
import { ColumnDefinition } from '../../models/column';
import { ExportBaseService } from '../../gameday/services/export-base.service';
import { TabSettings } from '../../gameday/models/report';
import { GameDayRootReducerState } from '../../state/root.state';
import { getAllOrdersOrder } from '../mappers/all-orders.calculations';
import { getDealWithOrdersState } from '../../state/data/reducer';

// Headers data
const ordersHeaders: ColumnDefinition[] = [
  { label: 'Ord #', field: 'orderNumber', position: 1 },
  { label: 'Manager', field: 'manager', position: 2 },
  { label: 'Series', field: 'series', position: 3 },
  {
    label: 'Maturity',
    field: 'maturityDate',
    position: 4
  },
  { label: 'Coupon', field: 'coupon', position: 5 },
  { label: 'Type', field: 'priority', position: 6 },
  { label: 'Priority', field: 'orderType', position: 7 },
  { label: 'Account', field: 'account', position: 8 },
  //{ label: 'Zip Code', field: 'zipCode', position: 9 },
  { label: 'Amt ($M)', field: 'amount', position: 9 },
  { label: 'Comments', field: 'comments', position: 10 }
];
const ORDERS_TOTALS_DEFAULT_COLUMNS = {
  manager: '',
  series: '',
  maturityDate: '',
  coupon: '',
  priority: '',
  orderType: '',
  account: '',
  //zipCode: ''
};
const TOTAL_LABEL = 'Total';
const ORDERS_LABEL = 'Orders';
const ORDERS_TAB = {
  name: ORDERS_LABEL,
  priority: 1,
  orderMapper: getAllOrdersOrder
};

@Injectable()
export class ExportOrdersService extends ExportBaseService {
  private readonly dealWithOrders: Observable<
    GameDayModel.RawData
  > = this.store.pipe(
    select(getDealWithOrdersState),
    filter(({ deal }: GameDayModel.RawData) => !!deal),
    distinctUntilChanged()
  );

  public readonly deal$: Observable<
    GameDayModel.Deal
  > = this.dealWithOrders.pipe(map(({ deal }: GameDayModel.RawData) => deal));

  // Table data stream
  public readonly ordersData$: Observable<
    TabSettings[]
  > = this.dealWithOrders.pipe(
    map(({ orders }: GameDayModel.RawData) => this.getTabs(orders))
  );

  public readonly isAllowed$: Observable<boolean> = this.dealWithOrders.pipe(
    map(
      ({ deal, orders }: GameDayModel.RawData) =>
        deal.gamedaySettings.allowExport && !!orders
    ),
    distinctUntilChanged()
  );

  constructor(private store: Store<GameDayRootReducerState>) {
    super();
  }

  private getTabs(orders: GameDayModel.Order[]): TabSettings[] {
    const orderTotals = {
      ...ORDERS_TOTALS_DEFAULT_COLUMNS,
      orderNumber: `${TOTAL_LABEL} (${(orders && orders.length) || ''})`,
      amount: orders
        .map(({ amount }) => amount)
        .reduce((previous, current) => previous + current, 0)
    };

    return [
      this.createTab({
        ...ORDERS_TAB,
        header: ordersHeaders,
        totals: toMillion(orderTotals),
        data: orders
      })
    ];
  }
}
